import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  type Event,
  getEvents,
  getEventSeriesForEvent,
  deleteEvent,
  formatEventDate,
  formatEventTime,
  formatCurrency,
  isEventUpcoming,
  isEventPast,
} from '../../features/events';
import { getImageUrlById } from '../../features/images';
import { Card, DashboardPageHeader, DeleteConfirmModal } from '../../shared/ui';
import EventModal from '../../components/EventModal';
import EventRsvpModal from '../../components/dashboard/EventRsvpModal';
import { getEventShareUrl, parseSocialLinksJson, socialPlatformMeta, getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';

type EventSeriesConfig = Awaited<ReturnType<typeof getEventSeriesForEvent>>;

function describeRecurrence(series: NonNullable<EventSeriesConfig>): string {
  const by = series.recurrence.byWeekday ?? [];
  const time = `${formatEventTime(series.timeStart)}–${formatEventTime(series.timeEnd)}`;

  if (series.recurrence.frequency === 'Daily') {
    const base = series.recurrence.interval === 1 ? 'Every day' : `Every ${series.recurrence.interval} days`;
    return `${base} · ${time}`;
  }
  if (series.recurrence.frequency === 'Monthly') {
    const base = series.recurrence.interval === 1 ? 'Every month' : `Every ${series.recurrence.interval} months`;
    return `${base} · ${time}`;
  }
  // Weekly
  const base = series.recurrence.interval === 1 ? 'Every week' : `Every ${series.recurrence.interval} weeks`;
  const days = by.length ? by.join(', ') : 'Weekly';
  return `${base} (${days}) · ${time}`;
}

const EventsPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [events, setEvents] = useState<Event[]>([]);
  const [seriesById, setSeriesById] = useState<Record<string, NonNullable<EventSeriesConfig>>>({});
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPublic, setFilterPublic] = useState<'all' | 'public' | 'private'>('all');
  const [filterPaid, setFilterPaid] = useState<'all' | 'paid' | 'free'>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [rsvpEvent, setRsvpEvent] = useState<Event | null>(null);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);

  const canView = !!profile && can(profile, 'events', 'view');
  const canEdit = !!profile && can(profile, 'events', 'edit');
  const canDelete = !!profile && can(profile, 'events', 'delete');

  useEffect(() => {
    if (canView) {
      getEvents()
        .then(setEvents)
        .catch((err) => {
          logErrorInDev('Failed to load events', err);
          showToast(getUserFriendlyErrorMessage(err, 'Failed to load events.'), 'error');
        });
    }
  }, [canView, showToast]);

  useEffect(() => {
    if (!canView || events.length === 0) return;
    const uniqueSeriesIds = Array.from(
      new Set(events.map((e) => e.eventSeriesId).filter((x): x is string => !!x))
    );
    const missing = uniqueSeriesIds.filter((id) => !seriesById[id]);
    if (missing.length === 0) return;

    const firstEventIdForSeries = (seriesId: string) =>
      events.find((e) => e.eventSeriesId === seriesId)?.id ?? null;

    Promise.all(
      missing
        .map((seriesId) => {
          const eventId = firstEventIdForSeries(seriesId);
          if (!eventId) return null;
          return getEventSeriesForEvent(eventId)
            .then((cfg) => (cfg ? { seriesId, cfg } : null))
            .catch(() => null);
        })
        .filter(Boolean) as Array<Promise<{ seriesId: string; cfg: NonNullable<EventSeriesConfig> } | null>>
    ).then((results) => {
      const next: Record<string, NonNullable<EventSeriesConfig>> = {};
      for (const r of results) {
        if (r?.cfg) next[r.seriesId] = r.cfg;
      }
      if (Object.keys(next).length) {
        setSeriesById((prev) => ({ ...prev, ...next }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, events]);

  const handleSaved = () => {
    showToast('Event saved successfully.');
    getEvents()
      .then(setEvents)
      .catch((err) => {
        logErrorInDev('Failed to refresh events', err);
        showToast(getUserFriendlyErrorMessage(err, 'Failed to refresh events.'), 'error');
      });
    setShowModal(false);
    setEditingEvent(null);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setShowModal(true);
  };

  const handleDeleteClick = (event: Event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    setIsDeleting(true);
    try {
      await deleteEvent(eventToDelete.id);
      setEvents((prev) => prev.filter((e) => e.id !== eventToDelete.id));
      setShowDeleteModal(false);
      setEventToDelete(null);
      showToast('Event deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete event', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete event.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setEventToDelete(null);
  };

  const copyEventLink = async (eventId: string) => {
    const url = getEventShareUrl(eventId);
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard.');
      setCopiedEventId(eventId);
      setTimeout(() => setCopiedEventId((prev) => (prev === eventId ? null : prev)), 1800);
      return;
    } catch {
      // Fallback for browsers/environments where clipboard API is unavailable.
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast('Link copied to clipboard.');
      setCopiedEventId(eventId);
      setTimeout(() => setCopiedEventId((prev) => (prev === eventId ? null : prev)), 1800);
    } catch {
      showToast('Unable to copy link. Copy it from the address bar if needed.', 'error');
    }
  };

  const filteredAndSortedEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const byFilters = events.filter((e) => {
      const matchesName =
        !term ||
        e.name.toLowerCase().includes(term) ||
        (e.description ?? '').toLowerCase().includes(term);
      const matchesPublic =
        filterPublic === 'all' ||
        (filterPublic === 'public' && e.isPublic) ||
        (filterPublic === 'private' && !e.isPublic);
      const matchesPaid =
        filterPaid === 'all' ||
        (filterPaid === 'paid' && e.isAPaidEvent) ||
        (filterPaid === 'free' && !e.isAPaidEvent);
      return matchesName && matchesPublic && matchesPaid;
    });

    return [...byFilters].sort((a, b) => {
      const da = new Date(a.dtDateOfEvent).getTime();
      const db = new Date(b.dtDateOfEvent).getTime();
      return sortDirection === 'asc' ? da - db : db - da;
    });
  }, [events, searchTerm, filterPublic, filterPaid, sortDirection]);

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2"></i>
            <strong>Access Restricted:</strong> You do not have permission to view events.
          </div>
        </div>
      </div>
    );
  }

  const upcomingEvents = filteredAndSortedEvents.filter((e) => isEventUpcoming(e.dtDateOfEvent));
  const pastEvents = filteredAndSortedEvents.filter((e) => isEventPast(e.dtDateOfEvent));

  const renderEventCard = (event: Event, isPast: boolean) => {
    const socialLinks = parseSocialLinksJson(event.socialLinksJson);
    const isRsvpOnly = event.isRsvpOnly ?? false;
    const hasExternalRsvp = !!(event.externalRsvpUrl?.trim());
    const showRsvpBtn =
      event.status === 'Published' &&
      isRsvpOnly &&
      !hasExternalRsvp;
    const series = event.eventSeriesId ? seriesById[event.eventSeriesId] : null;

    return (
      <div key={event.id} className="col-12 col-sm-6 col-xl-4">
        <div className={`class-offering-card ${isPast ? 'event-past' : ''}`}>
          <div className="class-offering-card__image-wrap">
            {event.imageId ? (
              <img
                src={getImageUrlById(event.imageId)}
                alt=""
              />
            ) : (
              <div className="class-offering-card__image-placeholder" aria-hidden>
                <i className="bi bi-image" />
              </div>
            )}
          </div>
          <div className="class-offering-card__body">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <h3 className="class-offering-card__title">{event.name}</h3>
              <div className="event-badges justify-content-end">
                {isPast && (
                  <span className="badge bg-secondary">
                    <i className="bi bi-check-circle me-1"></i>Past
                  </span>
                )}
                {!!event.eventSeriesId && (
                  <span className="badge bg-info text-dark">
                    <i className="bi bi-arrow-repeat me-1"></i>Series
                  </span>
                )}
                {event.isAPaidEvent && (
                  <span className="badge bg-warning text-dark">
                    <i className="bi bi-currency-exchange me-1"></i>Paid
                  </span>
                )}
                {isRsvpOnly && (
                  <span className="badge bg-primary">
                    <i className="bi bi-person-check me-1"></i>RSVP
                  </span>
                )}
              </div>
            </div>
            {(event.descriptionHtml || event.description) && (
              <div
                className="class-offering-card__subtitle mb-1 rich-text-preview"
                dangerouslySetInnerHTML={{
                  __html: event.descriptionHtml || event.description,
                }}
              />
            )}
            <p className="class-offering-card__date">
              <i className="bi bi-calendar3" />
              {formatEventDate(event.dtDateOfEvent)} · {formatEventTime(event.dtTimeStartOfEvent)} - {formatEventTime(event.dtTimeEndOfEvent)}
            </p>
            {event.eventSeriesId && series && (
              <p className="small text-muted mb-1">
                <i className="bi bi-arrow-repeat me-1" />
                {describeRecurrence(series)}
              </p>
            )}
            {event.isAPaidEvent && (
              <p className="small text-muted mb-1">
                <i className="bi bi-tag me-1" />
                {formatCurrency(event.priceOfEntry ?? 0)}
              </p>
            )}
            {socialLinks.length > 0 && (
              <div className="event-social-links mt-2">
                {socialLinks.map((link, idx) => {
                  const meta = socialPlatformMeta[link.platform] ?? socialPlatformMeta.custom;
                  return (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-social-link"
                      title={link.label || meta.title}
                    >
                      <i className={`bi ${meta.icon}`} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          <div className="class-offering-card__footer">
            <span className={`class-offering-card__badge ${event.isPublic ? 'class-offering-card__badge--public' : ''}`}>
              {event.isPublic ? 'Public' : isPast ? 'Past' : 'Private'}
            </span>
            <div className="class-offering-card__actions">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => copyEventLink(event.id)}
                title="Copy link to share"
              >
                <i className="bi bi-link-45deg me-1"></i>
                {copiedEventId === event.id ? 'Copied' : 'Copy link'}
              </button>
              {canEdit && (
                <button className="btn btn-success btn-sm" onClick={() => handleEdit(event)}>
                  <i className="bi bi-pencil me-1"></i>Edit
                </button>
              )}
              {canDelete && (
                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteClick(event)}>
                  <i className="bi bi-trash me-1"></i>Delete
                </button>
              )}
              {showRsvpBtn && (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => {
                    setRsvpEvent(event);
                    setShowRsvpModal(true);
                  }}
                >
                  <i className="bi bi-people me-1" />
                  RSVPs
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row events-management-page">
      <div className="col-12">
        <DashboardPageHeader
          className="events-page-toolbar"
          title="Events Management"
          description="Organize upcoming and past events in one clean view."
          icon="bi bi-calendar-event"
          actions={
            canEdit ? (
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
                <i className="bi bi-plus-circle me-1"></i>
                Add New Event
              </button>
            ) : null
          }
        >
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-search text-muted" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or description…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 180 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-globe text-muted" />
              </span>
              <select
                className="form-select"
                value={filterPublic}
                onChange={(e) => setFilterPublic(e.target.value as typeof filterPublic)}
              >
                <option value="all">All visibility</option>
                <option value="public">Public only</option>
                <option value="private">Private only</option>
              </select>
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 180 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-currency-exchange text-muted" />
              </span>
              <select
                className="form-select"
                value={filterPaid}
                onChange={(e) => setFilterPaid(e.target.value as typeof filterPaid)}
              >
                <option value="all">All price types</option>
                <option value="paid">Paid only</option>
                <option value="free">Free only</option>
              </select>
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 180 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-sort-down text-muted" />
              </span>
              <select
                className="form-select"
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value as typeof sortDirection)}
              >
                <option value="asc">Date ↑ (oldest first)</option>
                <option value="desc">Date ↓ (newest first)</option>
              </select>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setSearchTerm('');
                setFilterPublic('all');
                setFilterPaid('all');
                setSortDirection('asc');
              }}
            >
              <i className="bi bi-arrow-counterclockwise me-1" />
              Reset
            </button>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center events-page-toolbar__summary">
            <span className="badge rounded-pill text-bg-light border">
              Showing {filteredAndSortedEvents.length} of {events.length}
            </span>
            <span className="badge rounded-pill text-bg-light border">
              Upcoming {upcomingEvents.length}
            </span>
            <span className="badge rounded-pill text-bg-light border">
              Past {pastEvents.length}
            </span>
          </div>
        </DashboardPageHeader>

        {events.length === 0 ? (
          <Card title="Events" icon="bi bi-calendar-event">
            <div className="text-center py-4">
              <i className="bi bi-calendar-x fs-1 text-muted mb-3"></i>
              <p className="text-muted">No events found. Create your first event to get started.</p>
            </div>
          </Card>
        ) : (
          <>
            {upcomingEvents.length > 0 && (
              <Card title="Upcoming Events" icon="bi bi-calendar-event" className="mb-4">
                <div className="row g-3">
                  {upcomingEvents.map((e) => renderEventCard(e, false))}
                </div>
              </Card>
            )}
            {pastEvents.length > 0 && (
              <Card title="Past Events" icon="bi bi-calendar-check">
                <div className="row g-3">
                  {pastEvents.map((e) => renderEventCard(e, true))}
                </div>
              </Card>
            )}
            {filteredAndSortedEvents.length === 0 && (
              <Card title="No Matching Events" icon="bi bi-funnel">
                <div className="text-center py-4">
                  <i className="bi bi-search fs-1 text-muted mb-3"></i>
                  <p className="text-muted mb-2">No events match your current filters.</p>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearchTerm('');
                      setFilterPublic('all');
                      setFilterPaid('all');
                      setSortDirection('asc');
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {showModal && (
        <EventModal
          show={showModal}
          onHide={() => { setShowModal(false); setEditingEvent(null); }}
          onEventSaved={handleSaved}
          event={editingEvent}
          userId={user?.id ?? ''}
        />
      )}
      {showDeleteModal && eventToDelete && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onHide={cancelDelete}
          onConfirm={confirmDelete}
          itemName={eventToDelete.name}
          itemType="event"
          isLoading={isDeleting}
        />
      )}
      {showRsvpModal && rsvpEvent && (
        <EventRsvpModal
          show={showRsvpModal}
          onHide={() => {
            setShowRsvpModal(false);
            setRsvpEvent(null);
          }}
          eventId={rsvpEvent.id}
          eventName={rsvpEvent.name}
        />
      )}
    </div>
  );
};

export default EventsPage;
